/**
 * IDF Field Mappings for Geometry Objects
 *
 * Defines the field indices and names for EnergyPlus IDF geometry objects.
 * These mappings are used for creating and parsing IDF objects.
 */

/**
 * Zone object field mappings.
 * IDF Class: Zone
 */
export const ZONE_FIELD_MAPPINGS = {
  /** Field 0: Zone name */
  NAME: { index: 0, name: 'Name' },
  /** Field 1: Direction of relative north (degrees) */
  DIRECTION_OF_RELATIVE_NORTH: { index: 1, name: 'Direction of Relative North' },
  /** Field 2: X origin (meters) */
  X_ORIGIN: { index: 2, name: 'X Origin' },
  /** Field 3: Y origin (meters) */
  Y_ORIGIN: { index: 3, name: 'Y Origin' },
  /** Field 4: Z origin (meters) */
  Z_ORIGIN: { index: 4, name: 'Z Origin' },
  /** Field 5: Type */
  TYPE: { index: 5, name: 'Type' },
  /** Field 6: Multiplier */
  MULTIPLIER: { index: 6, name: 'Multiplier' },
  /** Field 7: Ceiling height */
  CEILING_HEIGHT: { index: 7, name: 'Ceiling Height' },
  /** Field 8: Volume */
  VOLUME: { index: 8, name: 'Volume' },
  /** Field 9: Floor area */
  FLOOR_AREA: { index: 9, name: 'Floor Area' },
  /** Field 10: Zone inside convection algorithm */
  ZONE_INSIDE_CONVECTION_ALGORITHM: { index: 10, name: 'Zone Inside Convection Algorithm' },
  /** Field 11: Zone outside convection algorithm */
  ZONE_OUTSIDE_CONVECTION_ALGORITHM: { index: 11, name: 'Zone Outside Convection Algorithm' },
  /** Field 12: Part of total floor area */
  PART_OF_TOTAL_FLOOR_AREA: { index: 12, name: 'Part of Total Floor Area' },
} as const;

/**
 * BuildingSurface:Detailed object field mappings.
 * IDF Class: BuildingSurface:Detailed
 */
export const SURFACE_FIELD_MAPPINGS = {
  /** Field 0: Surface name */
  NAME: { index: 0, name: 'Name' },
  /** Field 1: Surface type (Wall, Floor, Roof, Ceiling) */
  SURFACE_TYPE: { index: 1, name: 'Surface Type' },
  /** Field 2: Construction name */
  CONSTRUCTION_NAME: { index: 2, name: 'Construction Name' },
  /** Field 3: Zone name */
  ZONE_NAME: { index: 3, name: 'Zone Name' },
  /** Field 4: Space name (optional) */
  SPACE_NAME: { index: 4, name: 'Space Name' },
  /** Field 5: Outside boundary condition */
  OUTSIDE_BOUNDARY_CONDITION: { index: 5, name: 'Outside Boundary Condition' },
  /** Field 6: Outside boundary condition object */
  OUTSIDE_BOUNDARY_CONDITION_OBJECT: { index: 6, name: 'Outside Boundary Condition Object' },
  /** Field 7: Sun exposure */
  SUN_EXPOSURE: { index: 7, name: 'Sun Exposure' },
  /** Field 8: Wind exposure */
  WIND_EXPOSURE: { index: 8, name: 'Wind Exposure' },
  /** Field 9: View factor to ground */
  VIEW_FACTOR_TO_GROUND: { index: 9, name: 'View Factor to Ground' },
  /** Field 10: Number of vertices */
  NUMBER_OF_VERTICES: { index: 10, name: 'Number of Vertices' },
  /** Field 11+: Vertices start (X1, Y1, Z1, X2, Y2, Z2, ...) */
  VERTICES_START: { index: 11, name: 'Vertex 1 X-coordinate' },
} as const;

/**
 * FenestrationSurface:Detailed object field mappings.
 * IDF Class: FenestrationSurface:Detailed
 */
export const FENESTRATION_FIELD_MAPPINGS = {
  /** Field 0: Fenestration name */
  NAME: { index: 0, name: 'Name' },
  /** Field 1: Surface type (Window, Door, GlassDoor) */
  SURFACE_TYPE: { index: 1, name: 'Surface Type' },
  /** Field 2: Construction name */
  CONSTRUCTION_NAME: { index: 2, name: 'Construction Name' },
  /** Field 3: Building surface name (parent) */
  BUILDING_SURFACE_NAME: { index: 3, name: 'Building Surface Name' },
  /** Field 4: Outside boundary condition object */
  OUTSIDE_BOUNDARY_CONDITION_OBJECT: { index: 4, name: 'Outside Boundary Condition Object' },
  /** Field 5: View factor to ground */
  VIEW_FACTOR_TO_GROUND: { index: 5, name: 'View Factor to Ground' },
  /** Field 6: Frame and divider name */
  FRAME_AND_DIVIDER_NAME: { index: 6, name: 'Frame and Divider Name' },
  /** Field 7: Multiplier */
  MULTIPLIER: { index: 7, name: 'Multiplier' },
  /** Field 8: Number of vertices */
  NUMBER_OF_VERTICES: { index: 8, name: 'Number of Vertices' },
  /** Field 9+: Vertices start (X1, Y1, Z1, X2, Y2, Z2, ...) */
  VERTICES_START: { index: 9, name: 'Vertex 1 X-coordinate' },
} as const;

/**
 * Construction object field mappings.
 * IDF Class: Construction
 */
export const CONSTRUCTION_FIELD_MAPPINGS = {
  /** Field 0: Construction name */
  NAME: { index: 0, name: 'Name' },
  /** Field 1+: Layer names (outside to inside) */
  LAYERS_START: { index: 1, name: 'Outside Layer' },
} as const;

/**
 * Material object field mappings.
 * IDF Class: Material
 */
export const MATERIAL_FIELD_MAPPINGS = {
  /** Field 0: Material name */
  NAME: { index: 0, name: 'Name' },
  /** Field 1: Roughness */
  ROUGHNESS: { index: 1, name: 'Roughness' },
  /** Field 2: Thickness (m) */
  THICKNESS: { index: 2, name: 'Thickness' },
  /** Field 3: Conductivity (W/m-K) */
  CONDUCTIVITY: { index: 3, name: 'Conductivity' },
  /** Field 4: Density (kg/m3) */
  DENSITY: { index: 4, name: 'Density' },
  /** Field 5: Specific heat (J/kg-K) */
  SPECIFIC_HEAT: { index: 5, name: 'Specific Heat' },
  /** Field 6: Thermal absorptance */
  THERMAL_ABSORPTANCE: { index: 6, name: 'Thermal Absorptance' },
  /** Field 7: Solar absorptance */
  SOLAR_ABSORPTANCE: { index: 7, name: 'Solar Absorptance' },
  /** Field 8: Visible absorptance */
  VISIBLE_ABSORPTANCE: { index: 8, name: 'Visible Absorptance' },
} as const;

/**
 * Material:AirGap object field mappings.
 * IDF Class: Material:AirGap
 */
export const AIR_GAP_FIELD_MAPPINGS = {
  /** Field 0: Name */
  NAME: { index: 0, name: 'Name' },
  /** Field 1: Thermal resistance (m2-K/W) */
  THERMAL_RESISTANCE: { index: 1, name: 'Thermal Resistance' },
} as const;

/**
 * Surface type values for EnergyPlus.
 */
export const SURFACE_TYPES = {
  WALL: 'Wall',
  FLOOR: 'Floor',
  ROOF: 'Roof',
  CEILING: 'Ceiling',
} as const;

/**
 * Fenestration type values for EnergyPlus.
 */
export const FENESTRATION_TYPES = {
  WINDOW: 'Window',
  DOOR: 'Door',
  GLASSDOOR: 'GlassDoor',
} as const;

/**
 * Boundary condition values for EnergyPlus.
 */
export const BOUNDARY_CONDITIONS = {
  OUTDOORS: 'Outdoors',
  GROUND: 'Ground',
  ADIABATIC: 'Adiabatic',
  SURFACE: 'Surface',
  ZONE: 'Zone',
  OTHERSIDECOEFFICIENTS: 'OtherSideCoefficients',
  OTHERSIDECONDITIONSMODEL: 'OtherSideConditionsModel',
  GROUNDFCFACTORMETHOD: 'GroundFCfactorMethod',
  GROUNDSLABPREPROCESSORAVERAGE: 'GroundSlabPreprocessorAverage',
  GROUNDSLABPREPROCESSORCORE: 'GroundSlabPreprocessorCore',
  GROUNDSLABPREPROCESSORPERIMETER: 'GroundSlabPreprocessorPerimeter',
  GROUNDBASEMENTPREPROCESSORAVERAGEWALL: 'GroundBasementPreprocessorAverageWall',
  GROUNDBASEMENTPREPROCESSORAVERAGEFLOOR: 'GroundBasementPreprocessorAverageFloor',
  GROUNDBASEMENTPREPROCESSORUPPERWALL: 'GroundBasementPreprocessorUpperWall',
  GROUNDBASEMENTPREPROCESSORLOWERWALL: 'GroundBasementPreprocessorLowerWall',
  FOUNDATION: 'Foundation',
} as const;

/**
 * Sun exposure values for EnergyPlus.
 */
export const SUN_EXPOSURE = {
  SUN_EXPOSED: 'SunExposed',
  NO_SUN: 'NoSun',
} as const;

/**
 * Wind exposure values for EnergyPlus.
 */
export const WIND_EXPOSURE = {
  WIND_EXPOSED: 'WindExposed',
  NO_WIND: 'NoWind',
} as const;

/**
 * Convert surface type to EnergyPlus string.
 */
export function surfaceTypeToIDF(type: string): string {
  switch (type.toLowerCase()) {
    case 'wall':
      return SURFACE_TYPES.WALL;
    case 'floor':
      return SURFACE_TYPES.FLOOR;
    case 'roof':
      return SURFACE_TYPES.ROOF;
    case 'ceiling':
      return SURFACE_TYPES.CEILING;
    default:
      return SURFACE_TYPES.WALL;
  }
}

/**
 * Convert fenestration type to EnergyPlus string.
 */
export function fenestrationTypeToIDF(type: string): string {
  switch (type.toLowerCase()) {
    case 'window':
      return FENESTRATION_TYPES.WINDOW;
    case 'door':
      return FENESTRATION_TYPES.DOOR;
    case 'glassdoor':
      return FENESTRATION_TYPES.GLASSDOOR;
    default:
      return FENESTRATION_TYPES.WINDOW;
  }
}

/**
 * Convert boundary condition to EnergyPlus string.
 */
export function boundaryConditionToIDF(condition: string): string {
  const upper = condition.toUpperCase().replace(/-/g, '');
  const mapping: Record<string, string> = {
    OUTDOORS: BOUNDARY_CONDITIONS.OUTDOORS,
    GROUND: BOUNDARY_CONDITIONS.GROUND,
    ADIABATIC: BOUNDARY_CONDITIONS.ADIABATIC,
    SURFACE: BOUNDARY_CONDITIONS.SURFACE,
    ZONE: BOUNDARY_CONDITIONS.ZONE,
  };
  return mapping[upper] || BOUNDARY_CONDITIONS.OUTDOORS;
}

/**
 * Get sun exposure string based on boundary condition.
 */
export function getSunExposure(boundaryCondition: string): string {
  const noSunConditions = ['ground', 'surface', 'zone', 'adiabatic'];
  return noSunConditions.includes(boundaryCondition.toLowerCase())
    ? SUN_EXPOSURE.NO_SUN
    : SUN_EXPOSURE.SUN_EXPOSED;
}

/**
 * Get wind exposure string based on boundary condition.
 */
export function getWindExposure(boundaryCondition: string): string {
  const noWindConditions = ['ground', 'surface', 'zone', 'adiabatic'];
  return noWindConditions.includes(boundaryCondition.toLowerCase())
    ? WIND_EXPOSURE.NO_WIND
    : WIND_EXPOSURE.WIND_EXPOSED;
}
